import { useEffect, useState } from "react";
import { BarChart3, Loader2, PlayCircle, Scissors } from "lucide-react";
import { api } from "@/lib/api";

// Who presses play on the tour, and where they stop watching.
export const TourStats = () => {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setStats(null);
    api.get("/tour/stats", { params: { days } })
      .then(({ data }) => setStats(data))
      .catch((e) => console.error("could not load the tour stats", e));
  }, [days]);

  const top = stats?.plays || 1;

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="tour-stats">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
          <BarChart3 className="h-4 w-4 text-brand" /> Who watches the tour
        </p>
        <div className="ms-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`tour-stats-days-${d}`}
              onClick={() => setDays(d)}
              className={`min-h-[28px] rounded-full border px-3 text-[11px] transition-colors ${
                days === d ? "border-brand bg-brand/15 text-brand"
                  : "border-white/15 text-ink-soft hover:text-ink"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {!stats ? (
        <Loader2 className="mt-4 h-4 w-4 animate-spin text-ink-soft" />
      ) : stats.plays === 0 ? (
        <p className="mt-3 text-sm text-ink-soft" data-testid="tour-stats-empty">
          Nobody has pressed play in the last {days} days.
        </p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-6">
            <p className="text-sm" data-testid="tour-stats-plays">
              <span className="block font-display text-3xl font-bold text-brand">{stats.plays}</span>
              <span className="text-ink-soft">pressed play</span>
            </p>
            <p className="text-sm" data-testid="tour-stats-finished">
              <span className="block font-display text-3xl font-bold">{stats.finished}</span>
              <span className="text-ink-soft">watched to the end · {stats.through}%</span>
            </p>
          </div>

          <ul className="mt-5 space-y-1.5">
            {stats.scenes.map((scene, i) => (
              <li key={scene.key} className="flex items-center gap-2 text-[11px]"
                  data-testid={`tour-stats-scene-${scene.key}`}>
                <span className="w-6 shrink-0 tabular-nums text-ink-soft">{i + 1}</span>
                <span className="w-52 shrink-0 truncate text-ink" title={scene.title}>{scene.title}</span>
                <span className="h-1.5 min-w-[3px] rounded-full bg-brand/70"
                      style={{ width: `${Math.max(3, (scene.watchers / top) * 100)}%` }} />
                <span className={`shrink-0 tabular-nums ${
                  stats.trim?.key === scene.key ? "text-[#F5C451]" : "text-ink-soft"}`}>
                  {scene.watchers}
                  {scene.left ? ` · ${scene.left} left here · ${scene.quit_rate}%` : ""}
                </span>
              </li>
            ))}
          </ul>

          {stats.trim && (
            <p className="mt-4 rounded-2xl border border-[#F5C451]/30 bg-[#F5C451]/10 p-4 text-sm"
               data-testid="tour-trim">
              <Scissors className="me-2 inline h-4 w-4 text-[#F5C451]" />
              Chapter {stats.trim.at}, <b>“{stats.trim.title}”</b>, loses the most people —{" "}
              {stats.trim.left} of the {stats.trim.watchers} who reach it stop there
              ({stats.trim.quit_rate}%). Worth shortening or rewriting.
            </p>
          )}

          {stats.languages.length > 0 && (
            <p className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-ink-soft"
               data-testid="tour-stats-languages">
              <PlayCircle className="h-3.5 w-3.5" /> Languages chosen:
              {stats.languages.map((row) => (
                <span key={row.lang} className="rounded-full border border-white/10 px-2 py-0.5">
                  {row.lang} · {row.plays}
                </span>
              ))}
            </p>
          )}
        </>
      )}
    </div>
  );
};
